import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { useDebouncedValue } from '@eureca/ui/forms';
import { programByIdQueryOptions } from '../program-edit/program-by-id-query';
import {
  useCancelProgram,
  useDuplicateProgram,
  useEndProgram,
  usePublishProgram,
  type PublishVisibility,
} from '../program-edit/program-mutations';
import {
  PUBLISH_INCOMPLETE_JOBS_MESSAGE,
  usePublishReadiness,
} from '../program-edit/publish-readiness-query';
import { ProgramPublishDialog } from '../programs-list/ProgramPublishDialog';
import { useExportProgramBase } from '../program-export/use-export-program-base';
import { useExportAlignment } from '../program-alignment-share/use-export-alignment';
import { ShareAlignmentFormDialog } from '../program-alignment-share/ShareAlignmentFormDialog';
import { programExportAvailabilityQueryOptions } from '../program-export/program-export-availability';
import {
  bulkDocumentTypeOf,
  type ProgramBaseType,
} from '../program-export/program-export-availability';
import { useBulkDocumentExport } from '../program-export/use-bulk-document-export';

import { citiesSearchQueryOptions } from '@/features/jobs/job-edit-conditions/cities-query';
import { BRAZILIAN_UF_OPTIONS } from '@/features/jobs/job-edit-conditions/brazilian-ufs';
import {
  ProgramManagementPage as ProgramManagementPageView,
  type ProgramManagementDialog,
  type ProgramManagementView,
} from './ProgramManagementPage.component';
import {
  programCandidatesQueryOptions,
  type ProgramCandidateRow,
} from './program-candidates-query';
import {
  courseAreasCatalogQueryOptions,
  courseGroupsSearchQueryOptions,
  courseTypesCatalogQueryOptions,
} from './candidate-filter-catalogs';
import {
  EMPTY_CANDIDATE_FILTERS,
  hasAnyCandidateFilter,
  toQueryFilters,
  type CandidateFiltersState,
  type CandidateLocationChip,
} from './candidate-filters';
import {
  buildPhaseTabs,
  formatApplicationWindow,
  type ProgramManagementAction,
  type ProgramStatusKey,
} from './program-management-model';
import {
  SubscribersExportError,
  downloadSubscribersBase,
  subscribersExportErrorMessage,
} from './subscribers-export';
import { mePermissionsQueryOptions } from '@/shared/me-permissions-query';
import {
  PROGRAM_MANAGEMENT_DEFAULT_PAGE_SIZE,
  PROGRAM_MANAGEMENT_PAGE_SIZES,
} from './program-management-search.schema';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * EU-593 — container da tela "Gerenciar programa". Aba/busca/página vivem na
 * URL (shareáveis); os 11 campos do drawer vivem em estado local porque os
 * chips carregam labels resolvidos por autocomplete (ids crus na URL perderiam
 * o rótulo num cold-load — ver program-management-search.schema.ts).
 */
export function ProgramManagementPage() {
  const { programId } = useParams({ from: '/programas/$programId' });
  const search = useSearch({ from: '/programas/$programId' });
  const navigate = useNavigate();

  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? PROGRAM_MANAGEMENT_DEFAULT_PAGE_SIZE;
  const activeTab = search.tab ?? null;
  const appliedSearch = search.q ?? null;

  // Filtros aplicados (recorte da listagem) vs rascunho (drawer aberto).
  const [appliedFilters, setAppliedFilters] =
    useState<CandidateFiltersState>(EMPTY_CANDIDATE_FILTERS);
  const [filterDraft, setFilterDraft] = useState<CandidateFiltersState>(EMPTY_CANDIDATE_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedSubscriptionId, setExpandedSubscriptionId] = useState<string | null>(null);
  const [activeDialog, setActiveDialog] = useState<ProgramManagementDialog>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishVisibility, setPublishVisibility] = useState<PublishVisibility>('public');

  const programQuery = useQuery(programByIdQueryOptions(programId));
  // EU-593 — gate do "Exportar inscritos" (role `rank-consultor`, sem bypass de
  // admin). Fail-closed: enquanto carrega / em erro, `data` é undefined → false.
  const permissionsQuery = useQuery(mePermissionsQueryOptions());
  const canExportSubscribers = permissionsQuery.data?.canExportRankSubscribers ?? false;

  const filterActive = hasAnyCandidateFilter(appliedFilters);
  const candidatesQuery = useQuery(
    programCandidatesQueryOptions({
      programId,
      page,
      pageSize,
      stepTypeKey: activeTab ?? undefined,
      search: appliedSearch ?? undefined,
      ...toQueryFilters(appliedFilters),
    })
  );

  // Autocompletes do drawer.
  const [locationTerm, setLocationTerm] = useState('');
  const debouncedLocationTerm = useDebouncedValue(locationTerm, SEARCH_DEBOUNCE_MS);
  const citiesQuery = useQuery(citiesSearchQueryOptions(debouncedLocationTerm));
  const [courseGroupTerm, setCourseGroupTerm] = useState('');
  const debouncedGroupTerm = useDebouncedValue(courseGroupTerm, SEARCH_DEBOUNCE_MS);
  const courseGroupsQuery = useQuery(courseGroupsSearchQueryOptions(debouncedGroupTerm));
  const courseTypesQuery = useQuery(courseTypesCatalogQueryOptions());
  const courseAreasQuery = useQuery(courseAreasCatalogQueryOptions());

  // Localidade: UFs (locais) + cidades (server-side) no MESMO campo (GPR-041).
  const locationOptions = useMemo((): CandidateLocationChip[] => {
    const term = debouncedLocationTerm.trim().toLowerCase();
    const states =
      term.length >= 2
        ? BRAZILIAN_UF_OPTIONS.filter(
            (uf) => uf.label.toLowerCase().includes(term) || uf.value.toLowerCase() === term
          ).map(
            (uf): CandidateLocationChip => ({
              kind: 'state',
              stateAcronym: uf.value,
              label: `${uf.label} (${uf.value})`,
            })
          )
        : [];
    const cities = (citiesQuery.data ?? []).map(
      (city): CandidateLocationChip => ({
        kind: 'city',
        cityId: city.id,
        label: city.stateAcronym ? `${city.name} - ${city.stateAcronym}` : city.name,
      })
    );
    return [...states, ...cities];
  }, [debouncedLocationTerm, citiesQuery.data]);

  // Mutations de ciclo de vida (reuso EU-428/450/570).
  const publishProgram = usePublishProgram();
  const endProgram = useEndProgram();
  const cancelProgram = useCancelProgram();
  const duplicateProgram = useDuplicateProgram();
  const exportBase = useExportProgramBase();
  // DEV-498 — exportação em massa (assíncrona; 4 toasts; resultado por e-mail).
  const bulkExport = useBulkDocumentExport();
  // DEV-268 — "Exportar alinhamento" (4 Sonners) e o diálogo "Compartilhar formulário".
  const exportAlignment = useExportAlignment();
  const [shareAlignmentOpen, setShareAlignmentOpen] = useState(false);
  // EU-654 - dropdown dinamico (RN-02/03): disponibilidade eager (pagina de UM programa).
  const exportAvailability = useQuery(programExportAvailabilityQueryOptions(programId));
  const readiness = usePublishReadiness(programId, publishOpen);

  const isActionInFlight =
    publishProgram.isPending ||
    endProgram.isPending ||
    cancelProgram.isPending ||
    duplicateProgram.isPending;

  const program = programQuery.data;
  const statusKey = (candidatesQuery.data?.program.statusKey ??
    program?.statusKey ??
    'not_published') as ProgramStatusKey;

  const data = candidatesQuery.data;
  const hasRecorte = Boolean(appliedSearch) || filterActive;
  // "Sem candidatos" (estado do programa) ≠ zero resultados de busca/filtro.
  const hasCandidates = data ? data.tabs.all > 0 || hasRecorte : true;

  const view: ProgramManagementView = candidatesQuery.isError
    ? 'error'
    : !data && candidatesQuery.isPending
      ? 'loading'
      : data && data.items.length === 0
        ? hasRecorte
          ? 'empty-search'
          : 'empty-program'
        : 'rows';

  const tabs = useMemo(
    () =>
      buildPhaseTabs({
        availableStepTypeKeys: data?.availableStepTypeKeys ?? [],
        tabs: data?.tabs ?? { all: 0, byStepType: [] },
      }),
    [data?.availableStepTypeKeys, data?.tabs]
  );

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));

  function patchSearch(patch: Record<string, unknown>) {
    void navigate({
      to: '/programas/$programId',
      params: { programId },
      search: (prev) => ({ ...prev, ...patch }),
    });
  }

  function goToEditOrView() {
    // EU-570 — Encerrado/Cancelado abrem o MESMO fluxo em modo visualização.
    void navigate({ to: '/programas/$programId/editar', params: { programId } });
  }

  function handleAction(action: ProgramManagementAction) {
    switch (action) {
      case 'edit':
      case 'view':
        goToEditOrView();
        return;
      case 'publish':
        setPublishVisibility('public');
        setPublishOpen(true);
        return;
      case 'make-private':
        publishProgram.mutate(
          { programId, visibility: 'private' },
          {
            onSuccess: () =>
              toast('Programa privado', { action: { label: 'Fechar', onClick: () => undefined } }),
            onError: () => toast.error('Não foi possível privar o programa. Tente novamente.'),
          }
        );
        return;
      case 'make-public':
        publishProgram.mutate(
          { programId, visibility: 'public' },
          {
            onSuccess: () =>
              toast('Programa público', { action: { label: 'Fechar', onClick: () => undefined } }),
            onError: () =>
              toast.error('Não foi possível tornar o programa público. Tente novamente.'),
          }
        );
        return;
      case 'duplicate':
        duplicateProgram.mutate(programId, {
          onSuccess: (created) => {
            toast('Programa duplicado', {
              action: { label: 'Fechar', onClick: () => undefined },
            });
            void navigate({
              to: '/programas/$programId/editar',
              params: { programId: created.id },
              search: { copy: true },
            });
          },
          onError: () => toast.error('Não foi possível duplicar o programa. Tente novamente.'),
        });
        return;
      case 'end':
        setActiveDialog('end');
        return;
      case 'cancel':
        setActiveDialog('cancel');
        return;
    }
  }

  function handleConfirmEnd() {
    endProgram.mutate(programId, {
      onSuccess: () => {
        setActiveDialog(null);
        // GPR-028 — toast literal do Figma.
        toast('Programa encerrado', { action: { label: 'Fechar', onClick: () => undefined } });
        void candidatesQuery.refetch();
      },
      onError: () => toast.error('Não foi possível encerrar o programa. Tente novamente.'),
    });
  }

  function handleConfirmCancel() {
    cancelProgram.mutate(programId, {
      onSuccess: () => {
        setActiveDialog(null);
        // GPR-030 — toast literal do Figma.
        toast('Programa cancelado', { action: { label: 'Fechar', onClick: () => undefined } });
        void candidatesQuery.refetch();
      },
      onError: () => toast.error('Não foi possível cancelar o programa. Tente novamente.'),
    });
  }

  function handleConfirmPublish() {
    publishProgram.mutate(
      { programId, visibility: publishVisibility },
      {
        onSuccess: () => {
          setPublishOpen(false);
          toast('Programa lançado', { action: { label: 'Fechar', onClick: () => undefined } });
        },
        onError: () => toast.error('Não foi possível lançar o programa. Tente novamente.'),
      }
    );
  }

  /**
   * GPR-007 — Exportar inscritos: base CRUA via BFF (proxy do rank.eureca.me).
   * Download direto do `.xlsx`, sem influência de aba/busca/filtro e sem expor
   * o token ao browser (Opção B — o BFF encaminha o bearer e faz o stream de
   * volta; ver subscribers-export.ts). Erros do rank viram toast claro.
   */
  async function handleExportSubscribers() {
    try {
      await downloadSubscribersBase(programId);
    } catch (err) {
      const status = err instanceof SubscribersExportError ? err.status : 0;
      toast.error(subscribersExportErrorMessage(status));
    }
  }

  function handleExportResponses(type: ProgramBaseType) {
    // DEV-498 — "Currículos em massa" / "Laudos em massa": nada baixa.
    const bulkType = bulkDocumentTypeOf(type);
    if (bulkType) {
      bulkExport.mutate({ programId, type: bulkType });
      return;
    }
    exportBase.mutate({ programId, type });
  }

  async function copyToClipboard(value: string | null, successMessage: string, label: string) {
    if (!value) {
      toast.error(`${label} não cadastrado.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      // GPR-058 — nenhuma comunicação é disparada; só o clipboard + toast.
      toast(successMessage, { action: { label: 'Fechar', onClick: () => undefined } });
    } catch {
      toast.error(`Não foi possível copiar o ${label.toLowerCase()}.`);
    }
  }

  // GPR-053..060 × EU-594 — as ações do resumo rápido abrem a tela REAL de
  // Perfil do Candidato e os viewers dedicados (marca d'água + proxy
  // autenticado), preservando o `companyId` da área de trabalho. O Voltar do
  // perfil usa o histórico, então aba/busca/página desta listagem sobrevivem.
  function openFullProfile(row: ProgramCandidateRow) {
    void navigate({
      to: '/candidatos/$userId',
      params: { userId: row.userId },
      search: { companyId: search.companyId },
    });
  }

  function openResumeViewer(row: ProgramCandidateRow) {
    void navigate({
      to: '/candidatos/$userId/curriculo',
      params: { userId: row.userId },
      search: { companyId: search.companyId },
    });
  }

  function openAttestationViewer(row: ProgramCandidateRow) {
    void navigate({
      to: '/candidatos/$userId/laudo',
      params: { userId: row.userId },
      search: { companyId: search.companyId },
    });
  }

  const deadline = formatApplicationWindow(program?.startApplying, program?.endApplying);

  return (
    <>
      <ShareAlignmentFormDialog
        programId={programId}
        programName={data?.program.name ?? program?.name ?? ''}
        open={shareAlignmentOpen}
        onOpenChange={setShareAlignmentOpen}
        returnFocusTo={() =>
          document.querySelector<HTMLElement>('[data-testid="program-management-align-jobs"]')
        }
      />
      <ProgramManagementPageView
        programName={data?.program.name ?? program?.name ?? ''}
        statusKey={statusKey}
        deadline={deadline}
        companyLogoUrl={program?.company?.logoUrl ?? null}
        companyName={program?.company?.name ?? null}
        hasCandidates={hasCandidates}
        canExportSubscribers={canExportSubscribers}
        isActionInFlight={isActionInFlight}
        onAction={handleAction}
        onBack={() => void navigate({ to: '/programas' })}
        activeDialog={activeDialog}
        onDialogOpenChange={(open) => {
          if (!open) setActiveDialog(null);
        }}
        onConfirmEnd={handleConfirmEnd}
        onConfirmCancel={handleConfirmCancel}
        onExportSubscribers={handleExportSubscribers}
        onExportResponses={handleExportResponses}
        onEditAlignmentForm={() =>
          void navigate({ to: '/programas/$programId/alinhamento/editar', params: { programId } })
        }
        onShareAlignmentForm={() => setShareAlignmentOpen(true)}
        onExportAlignment={() => exportAlignment.mutate({ programId })}
        exportResponseTypes={exportAvailability.data}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(stepTypeKey) =>
          patchSearch({ tab: stepTypeKey ?? undefined, page: undefined })
        }
        countsFiltered={hasRecorte}
        appliedSearch={appliedSearch}
        onOpenSearch={() => setSearchOpen(true)}
        onClearSearch={() => patchSearch({ q: undefined, page: undefined })}
        searchOpen={searchOpen}
        onSearchOpenChange={setSearchOpen}
        onSearchSubmit={(term) => patchSearch({ q: term, page: undefined })}
        filterActive={filterActive}
        onOpenFilter={() => {
          setFilterDraft(appliedFilters);
          setFilterOpen(true);
        }}
        filterDrawer={{
          open: filterOpen,
          onOpenChange: setFilterOpen,
          values: filterDraft,
          onChange: setFilterDraft,
          onApply: () => {
            // GPR-049 — aplica o recorte, fecha o drawer e volta pra página 1.
            setAppliedFilters(filterDraft);
            setFilterOpen(false);
            patchSearch({ page: undefined });
          },
          onClear: () => {
            // GPR-050 — esvazia todos os campos, inclusive chips.
            setFilterDraft(EMPTY_CANDIDATE_FILTERS);
          },
          locationOptions,
          onLocationSearch: setLocationTerm,
          locationLoading: citiesQuery.isFetching,
          courseTypeOptions: (courseTypesQuery.data?.courseTypes ?? []).map((t) => ({
            id: t.id,
            label: t.name,
          })),
          courseAreaOptions: (courseAreasQuery.data?.courseAreas ?? []).map((a) => ({
            id: a.id,
            label: a.name,
          })),
          courseGroupOptions: (courseGroupsQuery.data?.courseGroups ?? []).map((g) => ({
            id: g.id,
            label: g.name,
          })),
          onCourseGroupSearch: setCourseGroupTerm,
          courseGroupLoading: courseGroupsQuery.isFetching,
          appliedFilterActive: filterActive,
        }}
        view={view}
        rows={data?.items ?? []}
        expandedSubscriptionId={expandedSubscriptionId}
        onToggleRow={(subscriptionId) =>
          // GPR-054 — um resumo aberto por vez.
          setExpandedSubscriptionId((current) =>
            current === subscriptionId ? null : subscriptionId
          )
        }
        onCopyEmail={(row) => void copyToClipboard(row.email, 'E-mail copiado', 'E-mail')}
        onCopyPhone={(row) =>
          void copyToClipboard(row.preview.phone, 'Telefone copiado', 'Telefone')
        }
        onOpenResume={openResumeViewer}
        onOpenReport={openAttestationViewer}
        onOpenFullProfile={openFullProfile}
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        pageSizeOptions={PROGRAM_MANAGEMENT_PAGE_SIZES}
        onPageChange={(next) => patchSearch({ page: next === 1 ? undefined : next })}
        onPageSizeChange={(nextSize) =>
          patchSearch({
            pageSize: nextSize === PROGRAM_MANAGEMENT_DEFAULT_PAGE_SIZE ? undefined : nextSize,
            page: undefined,
          })
        }
        onRetry={() => void candidatesQuery.refetch()}
      />

      {/* GPR-003 — "Lançar programa" do rascunho (fluxo EU-450 reusado). */}
      <ProgramPublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        visibility={publishVisibility}
        onVisibilityChange={setPublishVisibility}
        onConfirm={handleConfirmPublish}
        error={
          readiness.data && !readiness.data.ready ? PUBLISH_INCOMPLETE_JOBS_MESSAGE : undefined
        }
        incompleteJobs={readiness.data?.incompleteJobs ?? []}
        isCheckingReadiness={readiness.isFetching}
      />
    </>
  );
}
